import React, {Fragment, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import Select from "react-select";
import * as XLSX from 'xlsx';
import * as databaseBackupActions from "../../../../redux/DatabaseBackups/actions";
import Button from "../../../../components/Button";
import Icon from "react-web-vector-icons";
import {isNullOrEmptyArray} from "../../../../utils/general";
import {getObjectFields} from "./fields";
import {LinkButton} from "../../../../components/FormFields";
import Toaster from "../../../../components/Toaster";
import LoadingPanel from "../../../../components/LoadingPanel";

import './LoadData.scss';

const LoadData = props => {
	const { sandbox, onCancel } = props;
	const [loading, setLoading] = useState(false);
	const [inputFileHeaderRow, setInputFileHeaderRow] = useState([]);
	const [inputFileFirstDataRow, setInputFileFirstDataRow] = useState([]);
	const [file, setFile] = useState(null);
	const [matchedFields, setMatchedFields] = useState(null);
	const [selectedCloud, setSelectedCloud] = useState(null);
	const [selectedObject, setSelectedObject] = useState(null);
	const [selectedAction, setSelectedAction] = useState(null);
	const [validationResult, setValidationResult] = useState(null);

	const selectStyles = {
		width: '300px',
		border: '1px solid #D3E1ED !important',
		borderShadow: '0px 0px 0px 0px transparent',
		borderRadius: '20px',
		backgroundColor: 'transparent',
		paddingLeft: '5px',
		outline: 'none'
	};

	const dispatch = useDispatch();
	const hiddenFileInput = React.useRef(null);

	const onChangeCloud = (obj) => {
		setSelectedCloud(obj);
		resetFields();
	};

	const onChangeObject = (obj) => {
		setSelectedObject(obj);
		resetFields();
	};

	const onChangeAction = (obj) => {
		setSelectedAction(obj);
		resetFields();
	};

	const resetFields = () => {
		setInputFileHeaderRow([]);
		setInputFileFirstDataRow([]);
		setValidationResult(null);
		setFile(null);

		if(hiddenFileInput.current) {
			hiddenFileInput.current.value = "";
		}
	};

	const onUpload = async () => {
		setLoading(true);
		const config = {
			database: sandbox.name,
			cloud: selectedCloud.value,
			action: selectedAction.value,
			object: selectedObject.value
		};

		let mappedFields = matchedFields.filter(f => f.fieldName != null);

		dispatch(databaseBackupActions.uploadData(config, file, mappedFields, false, dataUploadResult));
	};

	const onValidate = async () => {
		setLoading(true);
		const config = {
			database: sandbox.name,
			cloud: selectedCloud.value,
			action: selectedAction.value,
			object: selectedObject.value
		};

		let mappedFields = matchedFields.filter(f => f.fieldName != null);

		dispatch(databaseBackupActions.uploadData(config, file, mappedFields, true, dataUploadResult));
	};

	const clearErrors = () => {
		setValidationResult(null);
	};

	const downloadFile = () => {
		const link = document.createElement('a');
		link.href = `${process.env.REACT_APP_HOST_URL}/output/${validationResult.details.fileName}`;
		link.download = 'validate.xlsx';
		link.click();
		setValidationResult(null);
	};

	const dataUploadResult = async (result) => {
		setLoading(false);
		console.log("dataUploadResult: ", result);

		if(result.status === "dbError") {
			Toaster.showError(result.error);
		}
		else if(result.status === "ok") {
			setValidationResult(result);
			if(result.details.onlyValidate === true) {
				Toaster.showSuccess(`All rows (${result.details.totalRows}) are valid and ready for upload.`);
			}
			else {
				Toaster.showSuccess(`Data updated successfully!`);
				resetFields();
			}
		}
		else {
			setValidationResult(result);
			Toaster.showWarn(`Data is invalid!`);
		}
	};

	useEffect(() => {
		const load = async () => {
			if(selectedObject && !isNullOrEmptyArray(inputFileHeaderRow)) {
				let fields = getObjectFields(selectedCloud.value, selectedObject.value);
				const matchedFields = [];

				inputFileHeaderRow.forEach((header) => {
					const match = fields.find(f => f.label.toLowerCase() === header?.toString()?.toLowerCase());
					matchedFields.push({ excelHeader: header, fieldName: match?.value });
				});

				setMatchedFields(matchedFields);
			}
		};
		load();
	}, [selectedObject, inputFileHeaderRow]);

	const handleFileChange = async file => {
		setLoading(true);
		if(!file) {
			setLoading(false);
			return;
		}
		
		const reader = new FileReader();
		reader.onload = (e) => {
			const data = new Uint8Array(e.target.result);
			const workbook = XLSX.read(data, { type: "array", sheetRows: 2 });

			// Assuming the first sheet in the workbook
			const worksheet = workbook.Sheets[workbook.SheetNames[0]];

			// Convert the sheet to JSON
			const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
			console.log(jsonData);
			
			// Get the first row
			if (jsonData.length > 1) {
				setFile(file)
				setInputFileHeaderRow(jsonData[0]);
				setInputFileFirstDataRow(jsonData[1]);
				setLoading(false);
			} else {
				Toaster.showWarn("Input file must include a header row and at least 1 data row");
				setLoading(false);
			}
		};
		reader.readAsArrayBuffer(file);
	};

	const onFileClick = () => {
		resetFields();
		hiddenFileInput.current?.click();
	};

	const onFileChange = event => {
		const fileUploaded = event.target.files[0];
		handleFileChange(fileUploaded);
	};

	const updateMatchedFields = (excelHeader, matchedObjectValue) => {
		const i = matchedFields.findIndex(m => m.excelHeader === excelHeader);
		if(i > -1) {
			const clone = [...matchedFields];
			clone[i].fieldName = matchedObjectValue;
			setMatchedFields(clone);
		}
		setValidationResult(null);
	};

	const renderColumnsMatching = () => {
		let fields = getObjectFields(selectedCloud.value, selectedObject.value);

		return (
			<div className="load-data-matching-table">
				<div className="load-data-matching-table-header">Source column (Excel)</div>
				<div className="load-data-matching-table-header">Target column (Database)</div>
				<div className="load-data-matching-table-header">Example value</div>

				{ matchedFields.map((matchedField, i) => {
					const value = fields.find(f => f.value === matchedField.fieldName);

					return (
						<Fragment key={i}>
							<div><span style={{color: `${value && value.requiredForInsert ? 'red' : 'white'}`}}>*&nbsp;</span> {matchedField.excelHeader}</div>
							<div style={{display: 'block'}}>
								<Select
									styles={{ control: (baseStyles, state) => ({ ...baseStyles, ...selectStyles }) }}
									options={fields.sort((a, b) => a.label.localeCompare(b.label))}
									onChange={obj => updateMatchedFields(matchedField.excelHeader, obj.value)}
									value={value}
								/>
							</div>
							<div>{!isNullOrEmptyArray(inputFileFirstDataRow) && inputFileFirstDataRow.length > i ? inputFileFirstDataRow[i] : ''}</div>
						</Fragment>
					)
				}) }
			</div>
		)
	};

	const renderErrorRow = (error, i) => {
		return (
			<li key={i} className="load-data-errors-error-row">
				Row {error.row}: {error.errorMessage}.
			</li>
		)
	};

	let totalRows, passedRows, percent;

	if(validationResult) {
		totalRows = validationResult.details.totalRows;
		passedRows = totalRows - validationResult.details.failedRows;
		percent = Math.floor(passedRows / totalRows * 100) + '%';
	}

	return (
		<div className="load-data-container">

			{ loading && <LoadingPanel /> }

			{
				validationResult && validationResult.status === "failed" && (
					<div className="load-data-errors-wrapper">
						<div className="load-data-errors-container">
							<div className="load-data-errors-title">
								Uploaded file validation errors
							</div>
							<div className="load-data-errors-box">
								<br/>
								Parsed {(passedRows)} out of {totalRows} rows ({percent}).
							</div>
							<div className="load-data-errors-footer">
								<Button width={100} center onClick={downloadFile} type="blue">Download</Button>
								<Button width={100} center onClick={clearErrors} type="blue">Ok</Button>
							</div>
						</div>
					</div>
				)
			}

			<div className="load-data-title">
				<div>Data Loader</div>
				<LinkButton style={{marginLeft: '10px'}} onClick={onCancel}>Cancel</LinkButton>
			</div>

			<div className="load-data-settings-table">
				<div>Target Database</div>
				<div>{sandbox.name}</div>
				<div>Cloud Provider</div>
				<div style={{width: '300px'}}>
					<Select
						styles={{ control: (baseStyles, state) => ({ ...baseStyles, ...selectStyles }) }}
						value={selectedCloud}
						onChange={obj => onChangeCloud(obj)}
						options={[
							{ value: 'aws', label: 'AWS' },
							{ value: 'ms', label: 'MS' }
						]}
					/>
				</div>
				<div>Object</div>
				<div style={{width: '300px'}}>
					<Select
						styles={{ control: (baseStyles, state) => ({ ...baseStyles, ...selectStyles }) }}
						value={selectedObject}
						onChange={obj => onChangeObject(obj)}
						options={[
							{ value: 'accounts', label: 'Accounts' },
							{ value: 'contacts', label: 'Contacts' },
							{ value: 'relations', label: 'Relations' }
						]}
					/>
				</div>
				<div>Action</div>
				<div style={{width: '300px'}}>
					<Select
						styles={{ control: (baseStyles, state) => ({ ...baseStyles, ...selectStyles }) }}
						value={selectedAction}
						onChange={obj => onChangeAction(obj)}
						options={[
							{ value: 'insert', label: 'Insert' },
							{ value: 'update', label: 'Update' },
							{ value: 'upsert', label: 'Upsert' },
							{ value: 'delete', label: 'Delete' }
						]}
					/>
				</div>
				{
					selectedObject &&
					<>
						<div>Source</div>
						<div>
							<div style={{display: 'flex', alignItems: 'center'}}>
								<div onClick={onFileClick} className="load-data-file-input">
									{file ? file.name : '[+] Upload File'}
								</div>
								{
									file != null &&
									<div style={{display: 'flex', alignItems: 'center', marginLeft: '50px'}}>
										{
											validationResult && validationResult.status === "ok" ?
												<Button leftFontIcon={<Icon name="pluscircleo" font="AntDesign" color="white" size={17}/>} onClick={onUpload} type="success">&nbsp;&nbsp;Upload Data</Button>
												:
												<Button leftFontIcon={<Icon name="pluscircleo" font="AntDesign" color="white" size={17}/>} onClick={onValidate} type="blue">&nbsp;&nbsp;Validate Data</Button>
										}
										<span style={{fontSize: '12px', margin: '0 20px', color: '#40A0C1', cursor: 'pointer', fontWeight: 'normal'}} onClick={resetFields}>X Reset</span>
									</div>
								}
							</div>

							<input type="file" accept=".csv,.xls,.xlsx" ref={hiddenFileInput} style={{display: 'none'}} onChange={onFileChange} />
						</div>
					</>
				}
			</div>

			{ selectedObject && inputFileHeaderRow.length > 0 && inputFileFirstDataRow.length > 0 && renderColumnsMatching() }
		</div>
	)
};

export default LoadData;
